// @givmo/design-system — StatCard / MetricCard (SaaS layer).
//
// A dashboard metric tile: a small label, a big tabular-nums value with a delta chip aligned to the
// value baseline, a muted sublabel, and an optional leading icon in a tinted circle.
//
// Delta chip: `rounded-md` (NOT a pill), w500 / 12px, subtle-tint `{tone}-50` bg + `{tone}-700`
// text. The tone is driven by `deltaIntent` — `positive` (green), `negative` (red), or `neutral`
// (slate). Color carries MEANING: a rising count that isn't good (e.g. "more pending approvals")
// must be `neutral`, never red. The up/down arrow follows `deltaDirection` independently of color.
import type { ReactNode } from 'react';
import { ArrowDownRight, ArrowUpRight } from 'lucide-react';
import { cn } from '../lib/utils';
import { testIdProp, type TestIdProps } from '../lib/testid';
import { Icon } from './icon';

export type StatDeltaIntent = 'positive' | 'negative' | 'neutral';

export interface StatCardProps extends TestIdProps {
  label: ReactNode;
  value: ReactNode;
  /** Optional leading icon (Lucide component) rendered in a tinted brand circle. */
  icon?: ReactNode;
  /** Delta text (e.g. "+12.5%"). Rendered as a subtle-tint `rounded-md` chip by the value. */
  delta?: string;
  /** Arrow direction — independent of color. */
  deltaDirection?: 'up' | 'down';
  /**
   * Delta color meaning. Explicit value wins; otherwise up = positive / down = negative. Use
   * `neutral` when a rise/fall is not inherently good or bad (e.g. pending counts).
   */
  deltaIntent?: StatDeltaIntent;
  /** Muted helper text under the value (e.g. "vs. last month"). */
  sublabel?: ReactNode;
  className?: string;
}

const DELTA_CHIP: Record<StatDeltaIntent, string> = {
  positive: 'bg-success-50 text-success-700',
  negative: 'bg-danger-50 text-danger-700',
  neutral: 'bg-neutral-100 text-neutral-600',
};

export function StatCard({
  label,
  value,
  icon,
  delta,
  deltaDirection = 'up',
  deltaIntent,
  sublabel,
  className,
  testId,
}: StatCardProps) {
  const intent: StatDeltaIntent =
    deltaIntent ?? (deltaDirection === 'up' ? 'positive' : 'negative');

  return (
    <div
      className={cn(
        'flex flex-col gap-12 rounded-xl border border-border-subtle bg-surface p-20 shadow-xs',
        className,
      )}
      {...testIdProp(testId)}
    >
      <div className="flex items-center justify-between gap-12">
        <span className="font-sans text-s13 font-w500 tracking-normal text-text-tertiary">
          {label}
        </span>
        {icon != null && (
          <span className="flex size-32 shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brand-600">
            {icon}
          </span>
        )}
      </div>
      <div className="flex flex-col gap-4">
        <div className="flex items-baseline gap-8">
          <span className="font-sans text-s30 font-w700 leading-h1 tracking-givmo text-text-strong tabular-nums">
            {value}
          </span>
          {delta != null && (
            <span
              className={cn(
                'inline-flex items-center gap-2 rounded-md px-6 py-2 font-sans text-s12 font-w500 tracking-normal tabular-nums',
                DELTA_CHIP[intent],
              )}
            >
              <Icon
                icon={deltaDirection === 'up' ? ArrowUpRight : ArrowDownRight}
                size={12}
                aria-hidden
              />
              {delta}
            </span>
          )}
        </div>
        {sublabel != null && (
          <span className="font-sans text-s12 font-w400 tracking-normal text-text-tertiary">
            {sublabel}
          </span>
        )}
      </div>
    </div>
  );
}
